import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import { LiaKeySolid } from "react-icons/lia";
import { BsArrowCounterclockwise } from "react-icons/bs";
import { IoSettingsOutline } from "react-icons/io5";
import { FaUserCheck, FaArrowLeft } from "react-icons/fa";
import PersonalDetailsForm from "../../components/FormComponents/PersonalDetailsForm";
import FamilyDetailsForm from "../../components/FormComponents/FamilyDetailsForm";
import MaternalDetailsForm from "../../components/FormComponents/MaternalDetailsForm";
import OtherDetailsForm from "../../components/FormComponents/OtherDetailsForm";
import ChangePasswordForm from "../../components/FormComponents/ChangePasswordForm";
import PaymentHistory from "../../components/FormComponents/PaymentHistory";
import VerificationTab from "../../components/FormComponents/VerificationTab";
import { NavLink, useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { userImagesUpload, userProfileDetails } from "../../Redux/Actions/AuthAction";
import { useDispatch, useSelector } from "react-redux";
import { getDropdown, getUserPersonalDetails } from "../../Redux/Actions/UsersProfileAction";
import useTitle from "../../Hooks/useTitle";

const Profile = () => {
  useTitle('Profile');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const Auth = useSelector((state) => state.Auth);
  const UsersProfile = useSelector((state) => state.UsersProfile);
  const [castData, setCastData] = useState([]);
  const [religionData, setReligionData] = useState([]);
  const [dietData, setDietData] = useState([]);
  const [maritalData, setMaritalData] = useState([]);

  const [formData, setFormData] = useState({});

  // State to track if we need to scroll to sender image
  const [scrollToSenderImage, setScrollToSenderImage] = useState(false);
  const senderImageSectionRef = useRef(null);

  const transformApiData = (apiData) => ({
    firstname: apiData.firstname || '',
    lastname: apiData.lastname || '',
    email: apiData.email || '',
    gender: apiData.profile?.gender || "Male",
    date_of_birth: apiData.profile?.date_of_birth || '',
    religion: apiData.profile?.religion || '',
    cast: apiData.profile?.cast || '',
    height: parseFloat(apiData.profile?.height) || '',
    weight: parseFloat(apiData.profile?.weight) || '',
    country_code: apiData.profile?.country_code || '',
    phone_number: apiData.profile?.phone_number || '',
    city: apiData.profile?.city || '',
    country: apiData.profile?.country || '',
    years_in_country: apiData.profile?.years_in_country || '',
    visa_status: apiData.profile?.visa_status || '',
    nationality: apiData.profile?.nationality || '',
    diet: apiData.profile?.diet || '',
    marital_status: apiData.profile?.marital_status || '',
    live_with_family: apiData.profile?.live_with_family === 1 || apiData.profile?.live_with_family === "1",
    hobbies: apiData.profile?.hobbies || '',
    extra_skills: apiData.profile?.extra_skills || '',

    // Arrays - always initialize as array, never undefined
    educations: Array.isArray(apiData.educations) ? apiData.educations : [],
    highschools: Array.isArray(apiData.highschools) ? apiData.highschools : [],
    occupations: Array.isArray(apiData.occupations) ? apiData.occupations : [],
    rewards: Array.isArray(apiData.rewards) ? apiData.rewards : [],
    awards: Array.isArray(apiData.awards) ? apiData.awards : [],
    medals: Array.isArray(apiData.medals) ? apiData.medals : [],
    certifications: Array.isArray(apiData.certifications) ? apiData.certifications : [],

    // Family fields
    father_name: apiData.family?.father_name || '',
    father_occupation: apiData.family?.father_occupation || '',
    father_phone_number: apiData.family?.father_phone_number || '',
    father_country_code: apiData.family?.father_country_code || '',
    mother_name: apiData.family?.mother_name || '',
    mother_occupation: apiData.family?.mother_occupation || '',
    mother_phone_number: apiData.family?.mother_phone_number || '',
    mother_country_code: apiData.family?.mother_country_code || '',
    parents_native: apiData.family?.parents_native || '',
    parents_cast: apiData.family?.parents_cast || '',
    maternal_uncle_name: apiData.family?.maternal_uncle_name || '',
    maternal_uncle_occupation: apiData.family?.maternal_uncle_occupation || '',
    maternal_uncle_native_place: apiData.family?.maternal_uncle_native_place || '',
    maternal_phone_number: apiData.family?.maternal_phone_number || '',
    maternal_country_code: apiData.family?.maternal_country_code || '',

    // Other details
    partners_parents_origin_choice: apiData.other_details?.partners_parents_origin_choice || '',
    expected_education: apiData.other_details?.expected_education || '',
    senders_name: apiData.other_details?.senders_name || '',
    senders_country_code: apiData.other_details?.senders_country_code || '',
    senders_phone_number: apiData.other_details?.senders_phone_number || '',
    person_to_contact_name: apiData.other_details?.person_to_contact_name || '',
    person_to_contact_country_code: apiData.other_details?.person_to_contact_country_code || '',
    person_to_contact_phone: apiData.other_details?.person_to_contact_phone || '',

    // Social media - explicitly default to empty string
    instagram: apiData.other_details?.instagram || '',
    linkedin: apiData.other_details?.linkedin || '',
    snapchat: apiData.other_details?.snapchat || '',
    twitter: apiData.other_details?.twitter || '',
    facebook: apiData.other_details?.facebook || '',

    // Sender image - check both locations
    sender_image: apiData.other_details?.sender_image || apiData.sender_image || '',
  });

  // Sender Image States
  const [senderPreview, setSenderPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [senderImageError, setSenderImageError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user");

  // Check if coming from alert and scroll to sender image
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromAlert = urlParams.get('fromAlert') === 'true';

    if (fromAlert) {
      setScrollToSenderImage(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Scroll to sender image when flag is set and component is ready
  useEffect(() => {
    if (scrollToSenderImage && senderImageSectionRef.current && !UsersProfile.loading) {
      setTimeout(() => {
        senderImageSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setScrollToSenderImage(false);
      }, 500);
    }
  }, [scrollToSenderImage, UsersProfile.loading]);

  useEffect(() => {
    const fetchUserPersonalDetails = async () => {
      try {
        await dispatch(getUserPersonalDetails());
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    }
    fetchUserPersonalDetails();

    const fetchDropDown = async () => {
      try {
        const res = await dispatch(getDropdown());
        if (res?.data?.data?.cast) {
          setCastData(res.data.data.cast);
          setReligionData(res.data.data.religion);
          setDietData(res.data.data.diet);
          setMaritalData(res.data.data.marital)
        } else {
          toast.error("Failed to fetch cast data.");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch data.");
      }
    };

    fetchDropDown();
  }, [dispatch])

  useEffect(() => {
    if (UsersProfile?.personalDetails?.data) {
      const transformedData = transformApiData(UsersProfile.personalDetails.data);
      setFormData(transformedData);
      setEducationList(transformedData.educations || []);
      setOccupationList(transformedData.occupations || []);
      setAwardsList(transformedData.awards || []);
      setRewardsList(transformedData.rewards || []);
      setMedalsList(transformedData.medals || []);
      setCertificationsList(transformedData.certifications || []);
      setHighschoolList(transformedData.highschools || []);

      setImages(UsersProfile.personalDetails.data.images.length === 0 ? [null, null, null, null] : UsersProfile.personalDetails.data.images);

      // Initialize sender preview
      if (transformedData.sender_image) {
        setSenderPreview(transformedData.sender_image);
      } else {
        setSenderPreview(null);
      }

      const date = transformedData.date_of_birth.split("-");
      const height = transformedData.height.toString().split(".");
      setDateOfBirth({
        day: date[2],
        month: date[1],
        year: date[0],
      });
      setHeight({
        ft: height[0],
        in: height[1],
      });
    }
  }, [UsersProfile]);

  const iconMapping = {
    "Edit Profile": <IoSettingsOutline size={20} />,
    "Verification": <FaUserCheck size={20} />,
    "Change Password": <LiaKeySolid size={20} />,
    "Payment History": <BsArrowCounterclockwise size={20} />
  };

  const [images, setImages] = useState([null, null, null, null]);
  const [dateOfBirth, setDateOfBirth] = useState({ day: "01", month: "01", year: "1990" });
  const [height, setHeight] = useState({ ft: "5", in: "8" });

  // State for handling which tab is active
  const [activeSettingTab, setActiveSettingTab] = useState("Edit Profile");
  const { tabName } = useParams();

  useEffect(() => {
    switch (tabName) {
      case 'Change-Password':
        setActiveSettingTab("Change Password");
        break;
      case 'Edit-Profile':
        setActiveSettingTab("Edit Profile");
        break;
      case 'Payment-History':
        setActiveSettingTab("Payment History");
        break;
      case 'Verification':
        setActiveSettingTab("Verification");
        break;
      default:
        break;
    }
  }, [tabName]);

  const menuItems = [
    { label: "Edit Profile" },
    { label: "Verification" },
    { label: "Change Password" },
    { label: "Payment History" },
  ];

  const buttonRefs = useRef([]);

  const handleClick = (tabName, index) => {
    setActiveSettingTab(tabName);
    if (buttonRefs.current[index]) {
      buttonRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  // Phone and Country Handlers
  const handlePhoneNumberChange = (field, newPhone) => {
    setFormData((prev) => ({ ...prev, [field]: newPhone }));
  };

  const handleCodeChange = (field, newCode) => {
    setFormData((prev) => ({ ...prev, [field]: newCode }));
  };

  const handleCountryChange = (field, newCountry) => {
    setFormData((prev) => ({ ...prev, [field]: newCountry }));
  };

  // Dynamic Education Fields
  const [educationList, setEducationList] = useState([]);
  const handleAddEducation = () => {
    setEducationList((prev) => [
      ...prev,
      { degree: "", from_year: "", to_year: "", university_name: "", city: "", college: "" },
    ]);
  };
  const handleRemoveEducation = (index) => {
    setEducationList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleEducationChange = (index, event) => {
    const { name, value } = event.target;
    setEducationList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Awards Fields
  const [awardsList, setAwardsList] = useState([]);
  const handleAddAwards = () => {
    setAwardsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveAwards = (index) => {
    setAwardsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAwardsChange = (index, event) => {
    const { name, value } = event.target;
    setAwardsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Rewards Fields
  const [rewardsList, setRewardsList] = useState([]);
  const handleAddRewards = () => {
    setRewardsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveRewards = (index) => {
    setRewardsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleRewardsChange = (index, event) => {
    const { name, value } = event.target;
    setRewardsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Medals Fields
  const [medalsList, setMedalsList] = useState([]);
  const handleAddMedals = () => {
    setMedalsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveMedals = (index) => {
    setMedalsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleMedalsChange = (index, event) => {
    const { name, value } = event.target;
    setMedalsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Certifications Fields
  const [certificationsList, setCertificationsList] = useState([]);
  const handleAddCertifications = () => {
    setCertificationsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveCertifications = (index) => {
    setCertificationsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleCertificationsChange = (index, event) => {
    const { name, value } = event.target;
    setCertificationsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Occupation Fields
  const [occupationList, setOccupationList] = useState([]);
  const handleAddOccupation = () => {
    setOccupationList((prev) => [
      ...prev,
      { role: "", from_year: "", to_year: "", company_name: "" },
    ]);
  };
  const handleRemoveOccupation = (index) => {
    setOccupationList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleOccupationChange = (index, event) => {
    const { name, value } = event.target;
    setOccupationList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Highschool Fields
  const [highschoolList, setHighschoolList] = useState([]);
  const handleHighschoolChange = (index, event) => {
    const { name, value } = event.target;
    setHighschoolList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Form Handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "live_with_family") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value || '' }));
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setDateOfBirth((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeightChange = (event) => {
    const { name, value } = event.target;
    setHeight((prev) => ({ ...prev, [name]: value }));
  };

  // Sender Image Handlers
  const handleTakePhoto = async () => {
    setIsCameraOpen(true);
    setSenderImageError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: facingMode
        }
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (error) {
      console.error("Camera access error:", error);
      toast.error("Unable to access camera. Please allow camera permissions.");
    }
  };

  // const switchCamera = async () => {
  //   const newFacingMode = facingMode === "user" ? "environment" : "user";
  //   setFacingMode(newFacingMode);

  //   const currentStream = videoRef.current.srcObject;
  //   if (currentStream) {
  //     currentStream.getTracks().forEach(track => track.stop());
  //   }

  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({ 
  //       video: { 
  //         width: { ideal: 1920 },
  //         height: { ideal: 1080 },
  //         facingMode: newFacingMode
  //       } 
  //     });
  //     videoRef.current.srcObject = stream;
  //     videoRef.current.play();
  //   } catch (error) {
  //     console.error("Error switching camera:", error);
  //     toast.error("Unable to switch camera. Your device might not support multiple cameras.");
  //   }
  // };

  const capturePhoto = (imageData = null) => {
    if (imageData) {
      setIsCameraOpen(false);
      setSenderPreview(imageData);
      setFormData(prev => ({ ...prev, sender_image: imageData }));
      setSenderImageError("");
    } else {
      // Fallback to original implementation
      const video = videoRef.current;
      const canvas = canvasRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");

      if (facingMode === "user") {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (facingMode === "user") {
        context.setTransform(1, 0, 0, 1, 0, 0);
      }

      const capturedImageData = canvas.toDataURL("image/jpeg", 0.92);

      const stream = video.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      setIsCameraOpen(false);
      setSenderPreview(capturedImageData);
      setFormData(prev => ({ ...prev, sender_image: capturedImageData }));
      setSenderImageError("");
    }
  };

  const switchCamera = (newFacingMode = null) => {
    if (newFacingMode) {
      setFacingMode(newFacingMode);
    } else {
      setFacingMode(prev => prev === "user" ? "environment" : "user");
    }
  };
  const cancelCamera = () => {
    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setIsCameraOpen(false);
  };

  // Handle sender image removal
  const handleRemoveSenderImage = () => {
    setSenderPreview(null);
    setFormData(prev => ({ ...prev, sender_image: '' }));
    setSenderImageError("");
  };

  // Handle verification submission
  const handleVerificationSubmit = async () => {
    if (!formData.sender_image) {
      setSenderImageError("Verification photo is required");
      toast.error("Please take a verification photo to continue.");
      return;
    }

    try {
      const updatedFormData = {
        ...formData,
        is_verified: true,
        verification_date: new Date().toISOString().split('T')[0]
      };

      const res = await dispatch(userProfileDetails(updatedFormData));
      if (!res) {
        return toast.error('Failed to submit verification. Please try again.');
      }

      toast.success('Verification submitted successfully!');
      dispatch(getUserPersonalDetails());
    } catch (error) {
      console.error('Verification submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit verification.');
    }
  };

  // Main form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add sender image validation
    if (!formData.sender_image) {
      setSenderImageError("Sender image is required. Please take a photo to continue.");
      setTimeout(() => {
        senderImageSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      return toast.error("Sender image is required!");
    }

    const incompleteEducation = educationList.some(edu => !edu.degree);
    if (incompleteEducation) {
      return toast.error("Degree is required.");
    }

    const incompleteOccupation = occupationList.some(edu => !edu.role);
    if (incompleteOccupation) {
      return toast.error("Please fill the Role field in occupation.");
    }

    const incompleteHighschools = highschoolList.some(hs => {
      const { school_name, from_year, to_year, city } = hs;
      const anyFilled = school_name || from_year || to_year || city;
      const allFilled = school_name && from_year && to_year && city;
      return anyFilled && !allFilled;
    });

    if (incompleteHighschools) {
      return toast.error("Please fill all field of highschool.");
    }

    const incompleteRewards = rewardsList.some(reward => {
      const { title, year } = reward;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });

    if (incompleteRewards) {
      return toast.error("If any reward is filled, both Title and Year are required.");
    }

    const incompleteAwards = awardsList.some(award => {
      const { title, year } = award;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteAwards) {
      return toast.error("If any award is filled, both Title and Year are required.");
    }

    const incompleteMedals = medalsList.some(medal => {
      const { title, year } = medal;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteMedals) {
      return toast.error("If any medal is filled, both Title and Year are required.");
    }

    const incompleteCertifications = certificationsList.some(cert => {
      const { title, year } = cert;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteCertifications) {
      return toast.error("If any certification is filled, both Title and Year are required.");
    }

    try {
      const imageData = new FormData();
      images.forEach((file, index) => {
        if (typeof file === "string") {
          imageData.append(`image_urls[]`, file);
        } else {
          imageData.append(`images[]`, file);
          imageData.append(`image_indexes[]`, index);
        }
      });

      const res1 = await dispatch(userImagesUpload(imageData));
      if (!res1) {
        return console.log('error while profile details submition: ')
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        const key = error?.response?.data?.errors;
        const firstField = Object.keys(key)[0];
        if (firstField && key[firstField].length > 0) {
          return toast.error(key[firstField][0]);
        }
      } else {
        return toast.error(error.response.data.message);
      }
    }

    const formattedDate = `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`;
    const formattedHeight = `${height.ft}.${height.in}`;

    const cleanedEducation = educationList.filter(edu => edu.degree && edu.degree.trim() !== "");
    const cleanedOccupation = occupationList.filter(occ => occ.role && occ.role.trim() !== "");
    const cleanedAwards = awardsList.filter(award => award.title && award.title.trim() !== "");
    const cleanedRewards = rewardsList.filter(reward => reward.title && reward.title.trim() !== "");
    const cleanedMedals = medalsList.filter(medal => medal.title && medal.title.trim() !== "");
    const cleanedCertifications = certificationsList.filter(cert => cert.title && cert.title.trim() !== "");
    const cleanedHighschools = highschoolList.filter(hs => hs.school_name && hs.school_name.trim() !== "");

    const updatedFormData = {
      ...formData,
      educations: cleanedEducation,
      occupations: cleanedOccupation,
      awards: cleanedAwards,
      rewards: cleanedRewards,
      medals: cleanedMedals,
      certifications: cleanedCertifications,
      highschools: cleanedHighschools,
      date_of_birth: formattedDate,
      height: formattedHeight,
      university_name: formData.university_name || '',
      college: formData.college || '',
      maternal_uncle_name: formData.maternal_uncle_name || '',
      maternal_phone_number: formData.maternal_phone_number || '',
      maternal_country_code: formData.maternal_country_code || '',
      instagram: formData.instagram || '',
      linkedin: formData.linkedin || '',
      snapchat: formData.snapchat || '',
      twitter: formData.twitter || '',
      facebook: formData.facebook || '',
      sender_image: formData.sender_image || '',
    };

    try {
      const res = await dispatch(userProfileDetails(updatedFormData));
      if (!res) {
        return toast.error('Something Went wrong ! can you try again after some time...!!!')
      }

      toast.success('Profile Updated Successfully!');
      dispatch(getUserPersonalDetails());
    } catch (error) {
      if (error?.response?.data?.errors) {
        const key = error?.response?.data?.errors;
        const firstField = Object.keys(key)[0];
        if (firstField && key[firstField].length > 0) {
          return toast.error(key[firstField][0]);
        }
      } else {
        return toast.error(error.response.data.message);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Mobile Settings: Visible only on small screens */}
      <div className="block lg:hidden mb-2">
        <div className="max-w-screen-xl mx-auto pb-2">
          <div className="flex text-xl items-center space-x-2 font-bold">
            <button
              onClick={() => window.history.back()}
              className="hover:text-[#E72848] transition-colors flex items-center group"
            >
              <FaArrowLeft
                size={20}
                className="group-hover:-translate-x-0.5 transition-transform"
              />
            </button>
            <span className="text-black">Settings</span>
          </div>
        </div>

        {/* Settings Menu Container */}
        <div className="flex items-center p-1 rounded-lg max-w-max justify-start overflow-auto gap-6 md:gap-2 border">
          {menuItems.map((item, index) => (
            <button
              key={item.label}
              ref={(el) => (buttonRefs.current[index] = el)}
              onClick={() => handleClick(item.label, index)}
              className={`flex flex-col items-center text-sm font-medium whitespace-nowrap p-3 rounded-md
                ${activeSettingTab === item.label
                  ? "text-[#E72848] bg-gray-100 border-[#E72848]"
                  : "text-gray-700"
                }
              `}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT SECTION */}
        <div className="w-full lg:w-auto hidden lg:block">
          <div className="sticky top-4">
            <Box className="p-4">
              <div className="max-w-screen-xl mx-auto">
                <div className="flex text-2xl items-center space-x-2 font-bold">
                  <button
                    onClick={() => window.history.back()}
                    className="hover:text-[#E72848] transition-colors flex items-center group"
                  >
                    <FaArrowLeft
                      size={20}
                      className="group-hover:-translate-x-0.5 transition-transform"
                    />
                  </button>
                  <span className="text-black">Settings</span>
                </div>
              </div>
              <div className="mt-4 w-full">
                {["Edit Profile", "Verification", "Change Password", "Payment History"].map((item) => (
                  <NavLink
                    to={`/profile/${item.split(" ").join("-")}`}
                    key={item}
                    onClick={() => setActiveSettingTab(item)}
                    className={`flex items-center gap-2 cursor-pointer p-3 rounded-xl font-bold my-2 w-full whitespace-nowrap 
                      ${activeSettingTab === item ? "text-white bg-[#E72848]" : "text-gray-700 bg-[#FFF]"}
                    `}
                  >
                    {iconMapping[item]}
                    {item}
                  </NavLink>
                ))}
              </div>
            </Box>
          </div>
        </div>

        <Divider orientation="vertical" flexItem className="hidden lg:block" />

        {/* RIGHT SECTION: Form */}
        {activeSettingTab === 'Edit Profile' && (
          !UsersProfile.loading && formData.email ? (
            <div className="w-full">
              <div className="w-full grid grid-cols-1 gap-5">
                <form className="w-full" onSubmit={handleSubmit}>
                  <PersonalDetailsForm
                    formData={formData}
                    handleFormChange={handleFormChange}
                    images={images}
                    setImages={setImages}
                    dateOfBirth={dateOfBirth}
                    handleDateChange={handleDateChange}
                    height={height}
                    handleHeightChange={handleHeightChange}
                    educationList={educationList}
                    handleEducationChange={handleEducationChange}
                    handleAddEducation={handleAddEducation}
                    handleRemoveEducation={handleRemoveEducation}
                    occupationList={occupationList}
                    handleOccupationChange={handleOccupationChange}
                    handleAddOccupation={handleAddOccupation}
                    handleRemoveOccupation={handleRemoveOccupation}
                    highschoolList={highschoolList}
                    handleHighschoolChange={handleHighschoolChange}
                    awardsList={awardsList}
                    handleAwardsChange={handleAwardsChange}
                    handleAddAwards={handleAddAwards}
                    handleRemoveAwards={handleRemoveAwards}
                    rewardsList={rewardsList}
                    handleRewardsChange={handleRewardsChange}
                    handleAddRewards={handleAddRewards}
                    handleRemoveRewards={handleRemoveRewards}
                    medalsList={medalsList}
                    handleMedalsChange={handleMedalsChange}
                    handleAddMedals={handleAddMedals}
                    handleRemoveMedals={handleRemoveMedals}
                    certificationsList={certificationsList}
                    handleCertificationsChange={handleCertificationsChange}
                    handleAddCertifications={handleAddCertifications}
                    handleRemoveCertifications={handleRemoveCertifications}
                    handlePhoneNumberChange={handlePhoneNumberChange}
                    handleCodeChange={handleCodeChange}
                    handleCountryChange={handleCountryChange}
                    castData={castData}
                    religionData={religionData}
                    dietData={dietData}
                    maritalData={maritalData}
                  />
                  <FamilyDetailsForm
                    formData={formData}
                    handleFormChange={handleFormChange}
                    handlePhoneNumberChange={handlePhoneNumberChange}
                    handleCodeChange={handleCodeChange}
                    handleCountryChange={handleCountryChange}
                    castData={castData}
                    errors={{
                      father_name: "Father name is required",
                      father_phone_number: "Invalid phone number format",
                      parents_cast: "Please select a cast"
                    }}
                  />
                  <MaternalDetailsForm
                    formData={formData}
                    handleFormChange={handleFormChange}
                    handleCodeChange={handleCodeChange}
                    handleCountryChange={handleCountryChange}
                    handlePhoneNumberChange={handlePhoneNumberChange}
                  />

                  {/* Sender Image Section with ref for scrolling */}
                  <div ref={senderImageSectionRef}>
                    <OtherDetailsForm
                      formData={formData}
                      handleFormChange={handleFormChange}
                      handlePhoneNumberChange={handlePhoneNumberChange}
                      handleCodeChange={handleCodeChange}
                      handleCountryChange={handleCountryChange}
                      senderPreview={senderPreview}
                      handleTakePhoto={handleTakePhoto}
                      isCameraOpen={isCameraOpen}
                      cancelCamera={cancelCamera}
                      capturePhoto={capturePhoto}
                      switchCamera={switchCamera}
                      facingMode={facingMode}
                      senderImageError={senderImageError}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onRemoveImage={handleRemoveSenderImage}
                    />
                  </div>

                  <div className="max-w-max m-auto">
                    <button
                      type="submit"
                      className="bg-[#E72848] text-[#FFF] py-2 px-12 rounded-lg font-bold flex justify-center transition-all duration-200 transform hover:scale-[1.02] active:scale-95 shadow-md hover:shadow-lg"
                    >
                      {Auth.loading ? (
                        <CircularProgress color="inherit" size={24} />
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className="h-[60vh] w-full flex items-center justify-center">
              <CircularProgress size={30} color="inherit" />
            </div>
          )
        )}

        {/* Verification Tab */}
        {activeSettingTab === 'Verification' && (
          <VerificationTab
            senderPreview={senderPreview}
            handleTakePhoto={handleTakePhoto}
            isCameraOpen={isCameraOpen}
            cancelCamera={cancelCamera}
            capturePhoto={capturePhoto}
            switchCamera={switchCamera}
            facingMode={facingMode}
            senderImageError={senderImageError}
            videoRef={videoRef}
            canvasRef={canvasRef}
            onRemoveImage={handleRemoveSenderImage}
            onSubmit={handleVerificationSubmit}
            loading={Auth.loading}
            updateFormData={handleFormChange} // This is crucial!
          />
        )}

        {/* Change Password Tab */}
        {activeSettingTab === 'Change Password' && (<ChangePasswordForm />)}

        {/* Payment History Tab */}
        {activeSettingTab === 'Payment History' && (<PaymentHistory />)}
      </div>
    </div>
  );
};

export default Profile;